import React, { useState } from 'react';
import { Box } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Text from '../Text';
import SectionHeader from '../SectionHeader';
import ScrollReveal from '../animated/ScrollReveal';
import MagneticButton from '../animated/MagneticButton';
import { colors, gradients } from '../../theme/designTokens';
import { aeraLayers, aeraMeta } from '../../data/aera';

const AeraPreview = () => {
  const [active, setActive] = useState(aeraLayers[0].id);
  const navigate = useNavigate();
  const current = aeraLayers.find((l) => l.id === active) || aeraLayers[0];

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 8, md: 12 },
        background: colors.white,
      }}
    >
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <ScrollReveal>
          <SectionHeader
            eyebrow={aeraMeta.name}
            title="The ALEgION Resilience Framework"
            highlightWords={['Resilience Framework']}
            subtitle={aeraMeta.tagline}
          />
        </ScrollReveal>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1.2fr' },
            gap: { xs: 3, md: 5 },
            alignItems: 'start',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {aeraLayers.map((layer) => {
              const isActive = layer.id === active;
              return (
                <Box
                  key={layer.id}
                  component={motion.button}
                  type="button"
                  onClick={() => setActive(layer.id)}
                  whileHover={{ x: 4 }}
                  sx={{
                    textAlign: 'left',
                    cursor: 'pointer',
                    border: 'none',
                    borderLeft: `2px solid ${isActive ? colors.navy : colors.borderLight}`,
                    background: isActive ? 'rgba(15,23,42,0.04)' : 'transparent',
                    py: 2,
                    px: 2.5,
                    transition: 'all 0.25s ease',
                  }}
                >
                  <Text variant="caption" sx={{ color: colors.slate, display: 'block', mb: 0.5 }}>
                    {layer.number}
                  </Text>
                  <Text variant="h4" color="primary">
                    {layer.title}
                  </Text>
                  <Text variant="bodySm" color="secondary">
                    {layer.subtitle}
                  </Text>
                </Box>
              );
            })}
          </Box>

          <AnimatePresence mode="wait">
            <Box
              key={current.id}
              component={motion.div}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
              sx={{
                p: { xs: 3, md: 4 },
                background: gradients.sectionDark,
                minHeight: 320,
              }}
            >
              <Text variant="eyebrow" sx={{ color: colors.ochre, mb: 2, display: 'block' }}>
                {current.number} · {current.title}
              </Text>
              <Text variant="h3" color="white" sx={{ mb: 2 }}>
                {current.subtitle}
              </Text>
              <Text variant="body" color="muted" sx={{ mb: 3, display: 'block' }}>
                {current.description}
              </Text>
              <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'rgba(255,255,255,0.7)' }}>
                {current.details.map((d) => (
                  <Box component="li" key={d} sx={{ mb: 1 }}>
                    <Text variant="bodySm" color="muted">
                      {d}
                    </Text>
                  </Box>
                ))}
              </Box>
            </Box>
          </AnimatePresence>
        </Box>

        <Box sx={{ textAlign: 'center', mt: 5 }}>
          <MagneticButton variant="navy" onClick={() => navigate('/framework')}>
            Explore AERA™ in Depth
          </MagneticButton>
        </Box>
      </Box>
    </Box>
  );
};

export default AeraPreview;
